import { useEffect, useRef, useState } from 'react';

/**
 * Lightweight scroll reveal hook using IntersectionObserver
 * @param {Object} options - Observer options
 * @param {number} options.threshold - Percentage of element that must be visible (0-1)
 * @param {string} options.rootMargin - Margin around root
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @returns {Object} - { ref, isVisible }
 */
export const useScrollReveal = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

/**
 * Hook for staggered animation of multiple items
 * @param {number} itemCount - Number of items to animate
 * @param {number} staggerDelay - Delay between each item (ms)
 * @returns {Array} - Array of visibility states
 */
export const useStaggerReveal = (itemCount, staggerDelay = 100) => {
  const { ref, isVisible } = useScrollReveal();
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    if (isVisible && visibleItems.length === 0) {
      // Trigger stagger effect
      for (let i = 0; i < itemCount; i++) {
        setTimeout(() => {
          setVisibleItems(prev => [...prev, i]);
        }, i * staggerDelay);
      }
    }
  }, [isVisible, itemCount, staggerDelay, visibleItems.length]);

  return { ref, visibleItems };
};
